import React, { useEffect, useState } from "react"
import { useStaticQuery, graphql, navigate } from "gatsby"
import styled from "styled-components"

import {
  BorderRadius,
  Color,
  Spacing,
  Typography,
} from "@persona-id/design-tokens"
import {
  ChevronDownIcon,
  ChevronRightIcon,
} from "@persona-id/design-tokens/icons/16px"
import {
  ContentfulCategory,
  ContentfulTopic,
  ContentfulSubtopic,
} from "../models/contentful"

const NavigationLeftArticles = ({
  currentCategory,
  currentTopic,
  currentSubtopic,
  onNavLinkClick,
}: {
  currentCategory: ContentfulCategory
  currentTopic: ContentfulTopic
  currentSubtopic: ContentfulSubtopic
  onNavLinkClick?: () => void
}) => {
  const data = useStaticQuery(graphql`
    query allCategoryQuery {
      allContentfulCategoryList(
        filter: { internalName: { eq: "All Categories" } }
      ) {
        nodes {
          categories {
            id
            title
            slug

            topics {
              id
              title
              slug

              subtopics {
                id
                title
                slug
              }
            }
          }
        }
      }
    }
  `)

  return (
    <>
      {data.allContentfulCategoryList.nodes[0].categories.map(
        (category: ContentfulCategory) => (
          <NavigationLeftCategory
            key={`navigation-left-category-${category.id}`}
            category={category}
            isCurrentCategory={currentCategory?.id === category.id}
            currentTopic={currentTopic}
            currentSubtopic={currentSubtopic}
            onNavLinkClick={onNavLinkClick}
          />
        )
      )}
    </>
  )
}

const NavigationLeftCategory = ({
  category,
  isCurrentCategory,
  currentTopic,
  currentSubtopic,
  onNavLinkClick,
}: {
  category: ContentfulCategory
  isCurrentCategory: boolean
  currentTopic: ContentfulTopic
  currentSubtopic: ContentfulSubtopic
  onNavLinkClick?: () => void
}) => {
  const [isExpanded, setIsExpanded] = useState(isCurrentCategory)

  // Expands the current category when the page changes
  useEffect(() => {
    if (isCurrentCategory) {
      setIsExpanded(true)
    }
  }, [isCurrentCategory])

  return (
    <>
      <NavigationLeftRowCategory
        isActive={isCurrentCategory}
        // only the deepest active row should be highlighted
        isActiveAndDeepestActive={isCurrentCategory && !currentTopic}
      >
        <NavigationLeftArrow onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
        </NavigationLeftArrow>
        <NavigationLeftLabel
          onClick={() => {
            navigate(`/${category.slug}`)
            if (onNavLinkClick) onNavLinkClick()
          }}
        >
          {category.title}
        </NavigationLeftLabel>
      </NavigationLeftRowCategory>
      {isExpanded &&
        category.topics.map(topic => (
          <NavigationLeftTopic
            key={`navigation-left-topic-${topic.id}`}
            topic={topic}
            isCurrentTopic={currentTopic?.id === topic.id}
            currentSubtopic={currentSubtopic}
            categorySlug={category.slug}
            onNavLinkClick={onNavLinkClick}
          />
        ))}
    </>
  )
}

const NavigationLeftTopic = ({
  topic,
  isCurrentTopic,
  currentSubtopic,
  categorySlug,
  onNavLinkClick,
}: {
  topic: ContentfulTopic
  isCurrentTopic: boolean
  currentSubtopic: ContentfulSubtopic
  categorySlug: string
  onNavLinkClick?: () => void
}) => {
  const [isExpanded, setIsExpanded] = useState(isCurrentTopic)

  // Expands the current topic when the page changes
  useEffect(() => {
    if (isCurrentTopic) {
      setIsExpanded(true)
    }
  }, [isCurrentTopic])

  return (
    <>
      <NavigationLeftRowTopic
        isActive={isCurrentTopic}
        isActiveAndDeepestActive={isCurrentTopic && !currentSubtopic}
      >
        <NavigationLeftArrow onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? (
            <ChevronDownIcon size={12} />
          ) : (
            <ChevronRightIcon size={12} />
          )}
        </NavigationLeftArrow>
        <NavigationLeftLabel
          onClick={() => {
            navigate(`/${categorySlug}/${topic.slug}`)
            if (onNavLinkClick) onNavLinkClick()
          }}
        >
          {topic.title}
        </NavigationLeftLabel>
      </NavigationLeftRowTopic>
      {isExpanded &&
        topic.subtopics.map(subtopic => (
          <NavigationLeftSubtopic
            key={`navigation-left-subtopic-${subtopic.id}`}
            subtopic={subtopic}
            isCurrentSubtopic={currentSubtopic?.id === subtopic.id}
            topicSlug={topic.slug}
            categorySlug={categorySlug}
            onNavLinkClick={onNavLinkClick}
          />
        ))}
    </>
  )
}

const NavigationLeftSubtopic = ({
  subtopic,
  isCurrentSubtopic,
  topicSlug,
  categorySlug,
  onNavLinkClick,
}: {
  subtopic: ContentfulSubtopic
  topicSlug: string
  isCurrentSubtopic: boolean
  categorySlug: string
  onNavLinkClick?: () => void
}) => (
  <NavigationLeftRowSubtopic
    isActive={isCurrentSubtopic}
    isActiveAndDeepestActive={isCurrentSubtopic}
    onClick={() => {
      navigate(`/${categorySlug}/${topicSlug}/${subtopic.slug}`)
      if (onNavLinkClick) onNavLinkClick()
    }}
  >
    {subtopic.title}
  </NavigationLeftRowSubtopic>
)

const NavigationLeftRow = styled.div<{
  isActive: boolean
  isActiveAndDeepestActive: boolean
}>`
  ${Typography.TextMedium};
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: ${BorderRadius.Sm};
  cursor: pointer;
  color: ${({ isActive }) =>
    isActive ? `${Color.CorePurple800}` : `${Color.CoreSlate1000}`};
  background-color: ${({ isActiveAndDeepestActive }) =>
    isActiveAndDeepestActive ? `${Color.CorePurple100}` : "none"};

  :hover {
    background-color: ${Color.CoreSlate50};
  }
`

const NavigationLeftRowCategory = styled(NavigationLeftRow)`
  margin-left: -${Spacing[1]};
  font-weight: 500;
`

const NavigationLeftRowTopic = styled(NavigationLeftRow)`
  margin-left: ${Spacing[2]};
`

const NavigationLeftRowSubtopic = styled(NavigationLeftRow)`
  margin-left: ${Spacing[5]};
  padding: ${Spacing[1]};
`

const NavigationLeftArrow = styled.div`
  display: flex;
  align-items: center;
  padding: ${Spacing[1]};
`

const NavigationLeftLabel = styled.div`
  padding: ${Spacing[1]};
  padding-left: 0px; // padding is on NavigationLeftArrow to provide sufficiently large click target
  flex-grow: 1;
`

export default NavigationLeftArticles
